import { combineReducers } from '@reduxjs/toolkit';

import { resetOnLogoutReducer } from '@utils';

import {
  CLEAR_PEOPLE,
  DESELECT_PERSON,
  ModalMode,
  SELECT_MULTIPLE_PERSONS,
  SELECT_PERSON,
  SET_SELECTED_ACTIVITY,
  activityValuesTabs,
} from '../../common';
import { detailModes } from '../constants';
import {
  CLOSE_HIKE,
  OPEN_CREATE_HIKE,
  OPEN_EDIT_HIKE,
  OPEN_RECORD_MILES,
  RECORD_HIKE_ERROR,
  RECORD_HIKE_REQUEST,
  RECORD_HIKE_RESPONSE,
  SET_ACTIVE_TAB,
  SET_DETAIL_MODE,
  SET_SELECTED_HIKE,
} from './actions';

const visible = (state = false, { type }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
    case OPEN_CREATE_HIKE:
    case OPEN_EDIT_HIKE: {
      return true;
    }
    case CLOSE_HIKE: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const modalMode = (state = ModalMode.RECORD, { type }) => {
  switch (type) {
    case OPEN_CREATE_HIKE: {
      return ModalMode.CREATE;
    }
    case OPEN_RECORD_MILES: {
      return ModalMode.RECORD;
    }
    case OPEN_EDIT_HIKE: {
      return ModalMode.EDIT;
    }
    default: {
      return state;
    }
  }
};

const loading = (state = false, { type }) => {
  switch (type) {
    case RECORD_HIKE_REQUEST: {
      return true;
    }
    case RECORD_HIKE_RESPONSE:
    case RECORD_HIKE_ERROR: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const userIds = (state = [], { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES: {
      return payload.userIds || [];
    }
    case CLEAR_PEOPLE:
    case OPEN_CREATE_HIKE:
    case OPEN_EDIT_HIKE: {
      return [];
    }

    case DESELECT_PERSON: {
      return state.filter(userId => userId != payload);
    }
    case SELECT_PERSON:
      return [...new Set([...state, payload])];

    case SELECT_MULTIPLE_PERSONS:
      return [...new Set([...state, ...payload])];
    default: {
      return state;
    }
  }
};

const dates = (state = {}, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
    case OPEN_EDIT_HIKE: {
      return {};
    }
    case OPEN_CREATE_HIKE: {
      return payload.dates;
    }
    default: {
      return state;
    }
  }
};

const selectedHike = (state = null, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
    case OPEN_CREATE_HIKE: {
      return null;
    }
    case SET_SELECTED_HIKE: {
      return payload;
    }
    case SET_DETAIL_MODE: {
      if (payload === detailModes.SUGGEST) {
        return null;
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

const activeTab = (state = activityValuesTabs.BASIC, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
    case OPEN_CREATE_HIKE: {
      return activityValuesTabs.BASIC;
    }
    case SET_ACTIVE_TAB: {
      return payload;
    }
    case SET_SELECTED_ACTIVITY: {
      const { areSameAdvancedValues = true } = payload || {};
      return areSameAdvancedValues
        ? activityValuesTabs.BASIC
        : activityValuesTabs.ADVANCED;
    }
    default: {
      return state;
    }
  }
};

const allowRemovePersons = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
    case OPEN_CREATE_HIKE:
      return payload.allowRemovePersons;
    case OPEN_EDIT_HIKE:
      return true;
    default:
      return state;
  }
};

const disableGetActivities = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
      return payload.disableGetActivities;
    case OPEN_CREATE_HIKE:
    case OPEN_EDIT_HIKE:
      return false;
    default:
      return state;
  }
};

const disableFutureDays = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
      return payload.disableFutureDays;
    case OPEN_CREATE_HIKE:
    case OPEN_EDIT_HIKE:
      return false;
    default:
      return state;
  }
};

const detailMode = (state = detailModes.SUGGEST, { type, payload }) => {
  switch (type) {
    case OPEN_RECORD_MILES:
      return detailModes.SUGGEST;
    case OPEN_CREATE_HIKE: {
      const { dates: { start } = {} } = payload;
      if (start) {
        return detailModes.CREATE;
      }
      return detailModes.SUGGEST;
    }
    case SET_DETAIL_MODE: {
      return payload;
    }
    case OPEN_EDIT_HIKE: {
      return detailModes.EDIT;
    }
    default: {
      return state;
    }
  }
};
const allowEditPersons = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_EDIT_HIKE:
    case OPEN_CREATE_HIKE: {
      const { allowEditOrAddPersonsPersons = false } = payload || {};
      return allowEditOrAddPersonsPersons;
    }
    case OPEN_RECORD_MILES: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const reducer = combineReducers({
  visible,
  modalMode,
  loading,
  userIds,
  dates,
  selectedHike,
  activeTab,
  detailMode,
  allowEditPersons,
  disableGetActivities,
  disableFutureDays,
  allowRemovePersons,
});

export default resetOnLogoutReducer(reducer);
